import { FC, useRef, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { GroupBase, OptionsOrGroups } from "react-select";

import StatsReports from "../statsReports";
import HubInputField from "src/components/inputType";

import { COMPARISON_LISTING, TIME_LISTING } from "src/constants/stats";
import { StatisticsPropsType, StatisticsSearchProps } from "./types";
import DatePicker from "react-datepicker";
import ReactInputMask from "react-input-mask";
import { format, parse } from "date-fns";



const CaseFilter: FC<StatisticsPropsType> = ({ title, defaultQueryParams, updateQueryParams }): JSX.Element => {
  const { control, handleSubmit, getValues, watch } = useForm<StatisticsSearchProps>({
    mode: "all",
    defaultValues: defaultQueryParams,
  });

  const startDateRef = useRef<DatePicker | null>(null)
  const endDateRef = useRef<DatePicker | null>(null)

  useEffect(() => {
    document.addEventListener("touchend", () => {
      if (startDateRef.current) {
        if (startDateRef.current.isCalendarOpen()) startDateRef.current.setOpen(false)
      }
      if (endDateRef.current) {
        if (endDateRef.current.isCalendarOpen()) endDateRef.current.setOpen(false)
      }
    });
    return () => document.removeEventListener("touchend", () => {
      if (startDateRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        if (startDateRef.current.isCalendarOpen()) startDateRef.current.setOpen(false)
      }
      if (endDateRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        if (endDateRef.current.isCalendarOpen()) endDateRef.current.setOpen(false)
      }
    });
  }, []);

  const start_date = watch("start_date");
  const end_date = watch("end_date");

  const searchForStats = (values: StatisticsSearchProps) => {
    const updatedValues = {
      ...values,
      period: values.period
        ? typeof values.period === "string"
          ? values.period
          : (values.period as unknown as { value: string; label: string }).value
        : "",
      soc_value:
        values.soc_value ?
          (typeof values.soc_value === "string" ? values.soc_value : (values.soc_value as unknown as { value: string; name: string; }).value)
          : "",
      drug_id:
        values.drug_id ?
          (typeof values.drug_id === "string" ? values.drug_id : (values.drug_id as unknown as { value: string; name: string; }).value)
          : "",
      filter_type: values.filter_type
        ? typeof values.filter_type === "string"
          ? values.filter_type
          : (values.filter_type as unknown as { value: string; label: string }).value
        : "",
      id:
        values.id ?
          (typeof values.id === "string" ? values.id : (values.id as unknown as { value: string; name: string; }).value)
          : "",
    };

    updateQueryParams(updatedValues, false);
  };



  return (
    <form onSubmit={handleSubmit(searchForStats)}>
      <div className="card mt-4">
        <div className="card-header">
          <h4 className="m-0">{title ? title : 'Statistics'}</h4>
        </div>

        <div className="card-body pt-3">
          <div className="row g-3 mb-3">
            <div className="col-12 col-md-3">
              <Controller
                name="start_date"
                control={control}
                defaultValue=""
                render={({ field }) => (
                    <DatePicker
                        selected={field.value ? new Date(field.value) : null} // Ensure correct date format
                        onChange={(date) => field.onChange(date?.toISOString().split("T")[0])} // Convert to YYYY-MM-DD for storing
                        dateFormat="MM/dd/yyyy"
                        className="form-control"
                        icon = "bi bi-calendar"
                        showMonthDropdown
                        showYearDropdown
                        customInput={<ReactInputMask mask="99/99/9999">{(inputProps: {}) =>
                            <input {...inputProps} />}</ReactInputMask>}
                        wrapperClassName="w-100"
                    />
                )}
              />
            </div>
            <div className="col-12 col-md-3">
              <Controller
                name="end_date"
                control={control}
                defaultValue=""
                render={({ field }) => (
                    <DatePicker
                        selected={field.value ? new Date(field.value) : null} // Convert string to Date
                        onChange={(date) => field.onChange(date?.toISOString().split("T")[0])} // Convert Date to YYYY-MM-DD string
                        className="form-control"
                        id="end_date"
                        icon = "bi bi-calendar"
                        showMonthDropdown
                        showYearDropdown
                        placeholderText="Select end date"
                        dateFormat="MM/dd/yyyy"
                        customInput={<ReactInputMask mask="99/99/9999">{(inputProps: {}) =>
                            <input {...inputProps} />}</ReactInputMask>}
                        wrapperClassName="w-100"
                    />
                )}
              />
            </div>
            <div className="col-12 col-md-2">
              <Controller
                name="period"
                control={control}
                defaultValue="day"
                render={({ field }) => (
                  <HubInputField
                    field={field}
                    inputId="period"
                    isRequired={false}
                    isRowBased={false}
                    isDropdown={true}
                    inputPlaceholder="Group Report By..."
                    isSearchable={false}
                    dropdownData={
                      TIME_LISTING.map((item) => ({
                        value: item.value,
                        label: item.label,
                      })) as unknown as OptionsOrGroups<string, GroupBase<string>>
                    }
                  />
                )}
              />
            </div>
            <div className="col-12 col-md-2">
              <Controller
                name="filter_type"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <HubInputField
                    field={field}
                    inputId="period"
                    isRequired={false}
                    isRowBased={false}
                    isDropdown={true}
                    inputPlaceholder="Compare With ..."
                    isSearchable={false}
                    dropdownData={
                      COMPARISON_LISTING.map((item) => ({
                        value: item.value,
                        label: item.label,
                      })) as unknown as OptionsOrGroups<string, GroupBase<string>>
                    }
                  />
                )}
              />
            </div>

            <div className="col-12 col-md-2 text-end">
              <button type="submit" className="btn btn-primary w-75">
                <i className="fas fa-sync-alt"></i> Refresh
              </button>
            </div>
          </div>

          <StatsReports getValues={getValues} />
        </div>
      </div>
    </form>
  );
};

export default CaseFilter;
